import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import {
    ArrowUp,
    BarChart3,
    Camera,
    ChevronDown,
    File as FileIcon,
    Film,
    Loader2,
    Palette,
    Plus,
    Sparkles,
    Wand2,
    X,
} from 'lucide-react'
import api, { type IOrdinalsMediaStreamItem } from 'api/api'
import answerModel from 'components/answers/answerModel'
import StaticAnswerBody from 'components/answers/StaticAnswerBody'
import OrdinalsRender from 'components/answers/FileEmbed/OrdinalsRender'
import questModel from 'components/quests/questModel'
import ErrorBoundary from 'components/shared/ErrorBoundary'
import UserIcon from 'components/users/UserIcon'
import useGetQuest from 'refactor/hooks/api/useGetQuest'
import useQuestChannel from 'state/channels/useQuestChannel'
import { cacheAnswer, getCachedAnswer } from 'state/cache'
import { draftAnswerStore } from 'state/draftAnswerStore'
import useStore from 'state/knovStore'
import type { Answer } from 'types/answers'
import type { Quest } from 'types/quests'
import {
    appendOrdinalChatDraftText,
    getOrdinalChatBodyDelta,
    getOrdinalChatReply,
    groupOrdinalChatAnswers,
    type OrdinalChatReplyReference,
    resolveOrdinalChatMediaState,
    withOrdinalChatReply,
    withoutOrdinalChatReply,
} from 'lib/ordinalChat'
import styles from './ordinal-chat-modal.module.scss'
import OrdinalChatUpvalueControl from './OrdinalChatUpvalueControl'
import { currentUserSnapshot } from 'lib/currentUserSnapshot'

type OrdinalChatModalProps = {
    item: IOrdinalsMediaStreamItem
    wrapperAnswer: Answer
    questId: string
    onClose: () => void
    onOpenBranch?: (target: { questId: string; answerId: string }) => void
}

type OpenMenu = 'plus' | 'make' | null
type CaretPreference = 'expanded' | 'docked' | null

const compactNumber = new Intl.NumberFormat('en-US', {
    notation: 'compact',
    maximumFractionDigits: 1,
})

const menuItems = {
    plus: [
        { id: 'camera', label: 'Camera', icon: Camera },
        { id: 'file', label: 'File', icon: FileIcon },
        { id: 'poll', label: 'Poll', icon: BarChart3 },
    ],
    make: [
        { id: 'illustrate', label: 'Illustrate', icon: Palette },
        { id: 'film', label: 'Film', icon: Film },
        { id: 'generate', label: 'Generate', icon: Wand2 },
    ],
} as const

function formatMessageTime(value: Date | string | number | null | undefined) {
    if (!value) return ''
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return ''
    return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
}

function ordinalTitle(item: IOrdinalsMediaStreamItem) {
    const id = String(item.id || '').replaceAll('-', '')
    return `Ordinal #${id.slice(0, 7) || '—'}`
}

function answerMediaUrls(answer: Answer) {
    const imageUrls = [
        answer.local_answer_image_url,
        answer.answer_image_url,
        ...(answer.images_v2 || []).map(
            image => image.urls?.list || image.urls?.original || image.preview_url || null,
        ),
        ...(answer.image_urls || []).map(image => image.image_url),
        ...(answer.file_urls || [])
            .filter(file => file.content_type?.startsWith('image/'))
            .map(file => file.preview_url || file.url),
    ].filter((url): url is string => typeof url === 'string' && !!url)

    return Array.from(new Set(imageUrls)).slice(0, 4)
}

function answerReplyThumbnail(answer: Answer) {
    return (
        answerMediaUrls(answer)[0] ||
        answer.recording_thumbnail_url ||
        answer.file_urls?.find(file => file.preview_url)?.preview_url ||
        undefined
    )
}

function AnswerMedia({ answer }: { answer: Answer }) {
    const images = answerMediaUrls(answer)
    const recordingUrl =
        (answer as Answer & { mp4_recording_url?: string | null }).mp4_recording_url ||
        answer.recording_url
    const files = (answer.file_urls || []).filter(file => !file.content_type?.startsWith('image/'))

    if (!images.length && !recordingUrl && !files.length) return null

    return (
        <div className={styles.messageMedia} data-count={images.length || undefined}>
            {images.length > 0 && (
                <div className={styles.messageImageGrid} data-count={images.length}>
                    {images.map(url => (
                        <img key={url} src={url} alt="Attached media" loading="lazy" />
                    ))}
                </div>
            )}
            {recordingUrl &&
                (answer.recording_content_type?.startsWith('audio/') ? (
                    <audio controls src={recordingUrl} />
                ) : (
                    <video controls playsInline src={recordingUrl} />
                ))}
            {files.map(file =>
                file.content_type?.startsWith('audio/') ? (
                    <audio key={file.url} controls src={file.url} />
                ) : file.content_type?.startsWith('video/') ? (
                    <video key={file.url} controls playsInline src={file.url} />
                ) : (
                    <a
                        key={file.url}
                        className={styles.fileAttachment}
                        href={file.url}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <FileIcon size={17} aria-hidden />
                        <span>{file.name || 'Attachment'}</span>
                    </a>
                ),
            )}
        </div>
    )
}

export default function OrdinalChatModal({
    item,
    wrapperAnswer,
    questId,
    onClose,
    onOpenBranch,
}: OrdinalChatModalProps) {
    const { quest, isFetching, isError, refetch } = useGetQuest(questId)
    useQuestChannel(questId, { reconcileOnSubscribe: true })

    useEffect(() => {
        void refetch()
    }, [questId, refetch])

    const sheetRef = useRef<HTMLDivElement>(null)
    const topChromeRef = useRef<HTMLDivElement>(null)
    const threadRef = useRef<HTMLDivElement>(null)
    const threadContentRef = useRef<HTMLDivElement>(null)
    const composerRef = useRef<HTMLFormElement>(null)
    const cameraInputRef = useRef<HTMLInputElement>(null)
    const fileInputRef = useRef<HTMLInputElement>(null)
    const messageInputRef = useRef<HTMLInputElement>(null)
    const flashTimeoutRef = useRef<number | null>(null)
    const dockedRef = useRef(false)
    const atBottomRef = useRef(true)
    const [message, setMessage] = useState('')
    const [sending, setSending] = useState(false)
    const [openMenu, setOpenMenu] = useState<OpenMenu>(null)
    const [caretPreference, setCaretPreference] = useState<CaretPreference>(null)
    const [scrollPinned, setScrollPinned] = useState(false)
    const [mediaHeight, setMediaHeight] = useState(0)
    const [docked, setDocked] = useState(false)
    const [layoutRevision, setLayoutRevision] = useState(0)
    const [reactionSheetOpen, setReactionSheetOpen] = useState(false)
    const [replyingTo, setReplyingTo] = useState<OrdinalChatReplyReference | null>(null)
    const [flashedRunId, setFlashedRunId] = useState<string | null>(null)
    const openAuthModal = useStore(state => state.actions.modalActions.openAuthModal)

    const answers = useMemo<Answer[]>(
        () =>
            quest
                ? questModel
                      .showUnarchivedAnswers(quest)
                      .filter(answer => answer && !answer.deleted && !answer.is_draft)
                : [],
        [quest],
    )
    const runs = useMemo(() => groupOrdinalChatAnswers(answers), [answers])

    useEffect(
        () => () => {
            if (flashTimeoutRef.current) window.clearTimeout(flashTimeoutRef.current)
        },
        [],
    )

    const armReply = useCallback((run: (typeof runs)[number]) => {
        const firstAnswer = run.answers[0]
        const lastAnswer = run.answers[run.answers.length - 1]
        const text = run.answers
            .map(answer => answerModel.getText(answer) || answer.content || '')
            .map(value => value.trim())
            .filter(Boolean)
            .join(' ')
        const mediaAnswer = run.answers.find(answer => answerReplyThumbnail(answer))
        const thumbnailUrl = mediaAnswer ? answerReplyThumbnail(mediaAnswer) : undefined
        setReplyingTo({
            answerId: String(lastAnswer.id),
            runId: run.id,
            name: firstAnswer.user?.name || 'Unknown',
            text: text || (thumbnailUrl ? 'Photo' : 'Message'),
            ...(thumbnailUrl ? { thumbnailUrl } : {}),
        })
        setOpenMenu(null)
        window.requestAnimationFrame(() => messageInputRef.current?.focus())
    }, [])

    const scrollToReply = useCallback((runId: string) => {
        const target = Array.from(
            threadContentRef.current?.querySelectorAll<HTMLElement>('[data-ordinal-chat-run]') ||
                [],
        ).find(node => node.dataset.ordinalChatRun === runId)
        if (!target) return
        target.scrollIntoView({ behavior: 'smooth', block: 'center' })
        setFlashedRunId(runId)
        if (flashTimeoutRef.current) window.clearTimeout(flashTimeoutRef.current)
        flashTimeoutRef.current = window.setTimeout(() => setFlashedRunId(null), 900)
    }, [])
    const participantCount = useMemo(
        () => new Set(runs.map(run => run.userId).filter(Boolean)).size,
        [runs],
    )
    const owner = item.parent?.user
    const ownerName = owner?.handle || owner?.username || owner?.name || 'unknown'
    const totalSats =
        Number(wrapperAnswer.bsv_upvalue_amt ?? item.parent?.bsv_upvalue_amt ?? 0) +
        Number(wrapperAnswer.minting_bsv_upvalue_amt ?? item.parent?.minting_bsv_upvalue_amt ?? 0)
    const previewUrl =
        item.preview_urls?.list || item.preview_urls?.original || item.preview_url || undefined
    const thumbnailUrl = item.preview_urls?.thumb || previewUrl
    const handleOrdinalPreviewReady = useCallback(() => setLayoutRevision(value => value + 1), [])

    const recalculateMedia = useCallback(() => {
        const sheet = sheetRef.current
        const topChrome = topChromeRef.current
        const threadContent = threadContentRef.current
        const composer = composerRef.current
        if (!sheet || !topChrome || !threadContent || !composer) return

        const desktop = window.innerWidth >= 900
        const ceiling = sheet.clientHeight * (desktop ? 0.5 : 0.55)
        if (caretPreference === 'docked') {
            dockedRef.current = true
            setDocked(true)
            setMediaHeight(0)
            return
        }
        if (caretPreference === 'expanded' || scrollPinned) {
            dockedRef.current = false
            setDocked(false)
            setMediaHeight(Math.round(ceiling))
            return
        }

        const next = resolveOrdinalChatMediaState({
            sheetHeight: sheet.clientHeight,
            headerHeight: topChrome.offsetHeight,
            composerHeight: composer.offsetHeight,
            threadContentHeight: threadContent.scrollHeight,
            wasDocked: dockedRef.current,
            desktop,
        })
        dockedRef.current = next.docked
        setDocked(next.docked)
        setMediaHeight(next.docked ? 0 : next.mediaHeight)
    }, [caretPreference, scrollPinned])

    useLayoutEffect(() => {
        recalculateMedia()

        const resizeObserver =
            typeof ResizeObserver !== 'undefined'
                ? new ResizeObserver(() => recalculateMedia())
                : null
        ;[
            sheetRef.current,
            topChromeRef.current,
            threadContentRef.current,
            composerRef.current,
        ].forEach(element => {
            if (element) resizeObserver?.observe(element)
        })
        window.addEventListener('resize', recalculateMedia)

        return () => {
            resizeObserver?.disconnect()
            window.removeEventListener('resize', recalculateMedia)
        }
    }, [answers.length, layoutRevision, recalculateMedia])

    useLayoutEffect(() => {
        const thread = threadRef.current
        if (!thread || !atBottomRef.current) return
        thread.scrollTop = thread.scrollHeight
    }, [answers.length])

    useEffect(() => {
        const handleQuestUpdated = (event: Event) => {
            const updatedQuestId = (event as CustomEvent<{ questId?: string }>).detail?.questId
            if (String(updatedQuestId) === String(questId)) void refetch()
        }
        window.addEventListener('quest_updated', handleQuestUpdated)
        return () => window.removeEventListener('quest_updated', handleQuestUpdated)
    }, [questId, refetch])

    useEffect(() => {
        sheetRef.current?.focus()
    }, [])

    useEffect(() => {
        const previousOverflow = document.body.style.overflow
        document.body.style.overflow = 'hidden'

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key !== 'Escape') return
            if (reactionSheetOpen) return
            if (openMenu) {
                setOpenMenu(null)
                return
            }
            onClose()
        }
        document.addEventListener('keydown', handleKeyDown)
        return () => {
            document.body.style.overflow = previousOverflow
            document.removeEventListener('keydown', handleKeyDown)
        }
    }, [onClose, openMenu, reactionSheetOpen])

    const openExpandedComposer = useCallback(
        ({ command, file, poll = false }: { command?: string; file?: File; poll?: boolean }) => {
            if (!quest) return
            if (!gon?.currentUser) {
                openAuthModal('signup', 'ordinal_thread')
                return
            }

            const existingDraftId = draftAnswerStore.getDraftId(quest.id)
            const existingDraft = existingDraftId ? getCachedAnswer(existingDraftId) : null
            const incomingText = `${message}${message && command ? ' ' : ''}${command || ''}`.trim()
            const replyReference = replyingTo
            const draft =
                existingDraft ||
                answerModel.newAnswer({
                    quest_id: quest.id,
                    space_id: quest.space_id,
                    is_draft: true,
                })
            const appendedDelta = incomingText
                ? appendOrdinalChatDraftText(draft.delta_json, incomingText)
                : draft.delta_json
            const nextDelta = appendedDelta
            const hydratedDraft = {
                ...draft,
                content: incomingText ? answerModel.getDeltaText(nextDelta) : draft.content,
                delta_json: nextDelta,
                ...(file ? { files: [...(draft.files || []), file] } : {}),
                ...(poll ? { message_type: 'poll', poll_options: ['', ''] } : {}),
            } as Answer

            cacheAnswer(hydratedDraft)
            draftAnswerStore.setDraft(quest.id, hydratedDraft.id)
            setMessage('')
            setReplyingTo(null)
            setOpenMenu(null)
            useStore.setState({
                isModalOpen: true,
                modalParams: {
                    type: 'NewAnswer',
                    noHeader: true,
                    quest,
                    newAnswer: hydratedDraft,
                    parentAnswer: quest.parent,
                    showReplyTo: false,
                    user: currentUserSnapshot(),
                    showUser: true,
                    showUsername: false,
                    onAnswerPosted: async (postedAnswer: Answer) => {
                        setMessage('')
                        if (replyReference && postedAnswer?.id) {
                            try {
                                await api.updateAnswer(postedAnswer.id, {
                                    space_id: postedAnswer.space_id || quest.space_id,
                                    content: postedAnswer.content,
                                    delta_json: withOrdinalChatReply(
                                        postedAnswer.delta_json || {
                                            ops: [{ insert: postedAnswer.content || '' }],
                                        },
                                        replyReference,
                                    ),
                                })
                            } catch (error) {
                                console.error('Failed to preserve ordinal chat reply quote', error)
                            }
                        }
                        void refetch()
                    },
                    customStyles: {
                        width: 'min(760px, 92dvw)',
                        minWidth: '0',
                        maxWidth: '760px',
                        maxHeight: '80dvh',
                        left: '50%',
                        transform: 'translate(-50%, 0%)',
                    },
                },
            })
        },
        [message, openAuthModal, quest, refetch, replyingTo],
    )

    const submitMessage = useCallback(async () => {
        const text = message.trim()
        if (!text || !quest || sending) return
        if (!gon?.currentUser) {
            openAuthModal('signup', 'ordinal_thread')
            return
        }

        const deltaJson = withOrdinalChatReply(
            { ops: [{ insert: text }, { insert: '\n' }] },
            replyingTo,
        )
        const draft = answerModel.newAnswer({
            quest_id: quest.id,
            space_id: quest.space_id,
            content: text,
            delta_json: deltaJson,
        }) as Answer
        const childQuest = questModel.newQuest({
            ...questModel.getPerms(quest),
            parent_id: draft.id,
            parent: draft,
            is_draft: false,
            isLocalChildQuest: true,
            space_id: quest.space_id,
            sorted_answers: [],
            sorted_answer_ids: [],
        })
        const payload = {
            ...draft,
            is_draft: false,
            content: text,
            delta_json: deltaJson,
            child_quests: [{ id: childQuest.id } as Quest],
        }
        setMessage('')
        setReplyingTo(null)
        setSending(true)
        atBottomRef.current = true
        try {
            await api.createAnswer(payload)
            await refetch()
        } catch (error) {
            setMessage(current => current || text)
            setReplyingTo(current => current || replyingTo)
            useStore
                .getState()
                .showGlobalNotice(
                    api.getReadableApiErrorMessage(error, 'Unable to post this message.'),
                    { variant: 'error', durationMs: 6500 },
                )
        } finally {
            setSending(false)
        }
    }, [message, openAuthModal, quest, refetch, replyingTo, sending])

    const handleThreadScroll = useCallback(() => {
        const thread = threadRef.current
        if (!thread) return
        const distanceFromBottom = thread.scrollHeight - thread.scrollTop - thread.clientHeight
        atBottomRef.current = distanceFromBottom < 24

        if (thread.scrollTop <= 0 && thread.scrollHeight > thread.clientHeight) {
            setScrollPinned(true)
        } else if (thread.scrollTop > 12) {
            setScrollPinned(false)
        }
    }, [])

    const handleMenuAction = (menu: Exclude<OpenMenu, null>, itemId: string) => {
        if (menu === 'plus') {
            if (itemId === 'camera') {
                setOpenMenu(null)
                cameraInputRef.current?.click()
            }
            if (itemId === 'file') {
                setOpenMenu(null)
                fileInputRef.current?.click()
            }
            if (itemId === 'poll') openExpandedComposer({ poll: true })
            return
        }

        const command =
            itemId === 'illustrate' ? '!illustrate ' : itemId === 'film' ? '!film ' : '!nb '
        openExpandedComposer({ command })
    }

    const modal = (
        <div
            className={styles.overlay}
            role="presentation"
            data-testid="ordinal-chat-overlay"
            onMouseDown={event => {
                if (event.target === event.currentTarget) onClose()
            }}
        >
            <section
                ref={sheetRef}
                className={styles.sheet}
                role="dialog"
                aria-modal="true"
                aria-label={`Chat about ${ordinalTitle(item)}`}
                data-testid="ordinal-chat-modal"
                tabIndex={-1}
            >
                <div ref={topChromeRef} className={styles.topChrome}>
                    <div className={styles.statusSpacer} aria-hidden />
                    <div className={styles.grabberRow} aria-hidden>
                        <span />
                    </div>
                    <header className={styles.header}>
                        <button
                            type="button"
                            className={styles.titleButton}
                            onClick={() =>
                                setCaretPreference(current =>
                                    current === 'expanded' || (!current && !docked)
                                        ? 'docked'
                                        : 'expanded',
                                )
                            }
                            aria-label={docked ? 'Expand ordinal' : 'Dock ordinal'}
                        >
                            <span className={styles.thumbnail} aria-hidden>
                                {thumbnailUrl ? <img src={thumbnailUrl} alt="" /> : null}
                            </span>
                            <span className={styles.titleStack}>
                                <span className={styles.titleLine}>
                                    <strong>{ordinalTitle(item)}</strong>
                                    <ChevronDown
                                        size={15}
                                        strokeWidth={2.4}
                                        className={docked ? styles.caretDocked : undefined}
                                        aria-hidden
                                    />
                                </span>
                                <span className={styles.metaLine}>
                                    <span>{ownerName}</span>
                                    {totalSats > 0 && <i aria-hidden>·</i>}
                                    {totalSats > 0 && (
                                        <strong>{compactNumber.format(totalSats)} sats</strong>
                                    )}
                                    {participantCount > 0 && <i aria-hidden>·</i>}
                                    {participantCount > 0 && <span>{participantCount} here</span>}
                                </span>
                            </span>
                        </button>
                        <button
                            type="button"
                            className={styles.closeButton}
                            onClick={onClose}
                            aria-label="Close ordinal chat"
                        >
                            <X size={19} strokeWidth={2.3} aria-hidden />
                        </button>
                    </header>
                </div>

                <div
                    className={styles.artifactRegion}
                    style={{ height: `${mediaHeight}px` }}
                    aria-hidden={docked}
                    data-docked={docked}
                >
                    <div className={styles.artifactFrame}>
                        <ErrorBoundary label={`Ordinal chat media ${item.id}`}>
                            <OrdinalsRender
                                outpoint={item.origin || ''}
                                url={`${window.knovApiUrl}/api/v1/bsv/ordinals/${item.id}/data`}
                                bsvOrdinalId={item.id}
                                contentType={item.content_type || ''}
                                previewUrl={previewUrl}
                                previewSize="list"
                                allowInteraction={false}
                                title={ordinalTitle(item)}
                                filename={item.filename || undefined}
                                className={styles.ordinalRender}
                                naturalVideo={item.content_type?.startsWith('video/')}
                                onPreviewReady={handleOrdinalPreviewReady}
                                audioPalette="orange"
                            />
                        </ErrorBoundary>
                        <span className={styles.artifactChip}>Ordinal</span>
                    </div>
                </div>

                <div ref={threadRef} className={styles.thread} onScroll={handleThreadScroll}>
                    <div ref={threadContentRef} className={styles.threadContent}>
                        {isFetching && !quest ? (
                            <div className={styles.threadState}>
                                <Loader2 size={20} className={styles.spinner} aria-hidden />
                                <span>Loading chat…</span>
                            </div>
                        ) : isError ? (
                            <div className={styles.threadState} role="alert">
                                This chat could not be loaded.
                            </div>
                        ) : runs.length === 0 ? (
                            <div className={styles.emptyThread}>
                                <strong>Start the conversation</strong>
                                <span>Share what this ordinal sparks for you.</span>
                            </div>
                        ) : (
                            runs.map(run => {
                                const firstAnswer = run.answers[0]
                                const signalAnswer = run.answers[run.answers.length - 1]
                                const branchAnswer = run.answers.find(
                                    answer => answer.child_quests?.length,
                                )
                                const branchQuest = branchAnswer?.child_quests?.[0]
                                const branchCount = run.answers.reduce(
                                    (total, answer) =>
                                        total + Number(answer.child_answer_count || 0),
                                    0,
                                )
                                const user = firstAnswer.user || {
                                    id: firstAnswer.user_id,
                                    name: 'Unknown',
                                }
                                return (
                                    <article
                                        key={run.id}
                                        className={`${styles.messageRun} ${
                                            flashedRunId === run.id ? styles.messageRunFlash : ''
                                        }`}
                                        data-ordinal-chat-run={run.id}
                                    >
                                        <UserIcon
                                            user={user}
                                            size="small"
                                            contextStyles={styles.avatar}
                                        />
                                        <div className={styles.runMain}>
                                            <div className={styles.runHeader}>
                                                <strong>{user.name || 'Unknown'}</strong>
                                                <time>
                                                    {formatMessageTime(firstAnswer.created_at)}
                                                </time>
                                            </div>
                                            <div className={styles.runMessages}>
                                                {run.answers.map(answer => {
                                                    const reply = getOrdinalChatReply(
                                                        answer.delta_json,
                                                    )
                                                    return (
                                                        <div
                                                            key={answer.id}
                                                            className={styles.messageBody}
                                                        >
                                                            {reply && (
                                                                <button
                                                                    type="button"
                                                                    className={styles.postedQuote}
                                                                    onClick={() =>
                                                                        scrollToReply(reply.runId)
                                                                    }
                                                                >
                                                                    <span
                                                                        className={styles.quoteRule}
                                                                        aria-hidden
                                                                    />
                                                                    {reply.thumbnailUrl && (
                                                                        <img
                                                                            src={reply.thumbnailUrl}
                                                                            alt=""
                                                                        />
                                                                    )}
                                                                    <span
                                                                        className={
                                                                            styles.quoteContent
                                                                        }
                                                                    >
                                                                        <strong>
                                                                            {reply.name}
                                                                        </strong>
                                                                        <span>{reply.text}</span>
                                                                    </span>
                                                                </button>
                                                            )}
                                                            <StaticAnswerBody
                                                                value={getOrdinalChatBodyDelta(
                                                                    answer.delta_json,
                                                                    answer.content || '',
                                                                )}
                                                                answer={answer}
                                                                type="answer"
                                                            />
                                                            <AnswerMedia answer={answer} />
                                                        </div>
                                                    )
                                                })}
                                            </div>
                                            <OrdinalChatUpvalueControl
                                                answer={signalAnswer}
                                                answers={run.answers}
                                                signalQuest={quest}
                                                onOpenChange={setReactionSheetOpen}
                                                onReactionCommitted={() => void refetch()}
                                                onReply={() => armReply(run)}
                                                branchCount={branchCount}
                                                onBranch={
                                                    branchQuest && branchAnswer
                                                        ? () =>
                                                              onOpenBranch?.({
                                                                  questId: String(branchQuest.id),
                                                                  answerId: String(branchAnswer.id),
                                                              })
                                                        : undefined
                                                }
                                            />
                                        </div>
                                    </article>
                                )
                            })
                        )}
                    </div>
                </div>

                {openMenu && (
                    <button
                        type="button"
                        className={`${styles.menuScrim} ${
                            replyingTo ? styles.menuScrimWithReply : ''
                        }`}
                        aria-label="Close composer menu"
                        onClick={() => setOpenMenu(null)}
                    />
                )}
                {openMenu && (
                    <div
                        className={`${openMenu === 'plus' ? styles.plusMenu : styles.makeMenu} ${
                            replyingTo ? styles.menuWithReply : ''
                        }`}
                        role="menu"
                    >
                        {menuItems[openMenu].map(({ id, label, icon: Icon }, index) => (
                            <React.Fragment key={id}>
                                {index > 0 && <div className={styles.menuDivider} />}
                                <button
                                    type="button"
                                    role="menuitem"
                                    onClick={() => handleMenuAction(openMenu, id)}
                                >
                                    <Icon size={20} strokeWidth={1.9} aria-hidden />
                                    <span>{label}</span>
                                </button>
                            </React.Fragment>
                        ))}
                    </div>
                )}

                <form
                    ref={composerRef}
                    className={styles.composer}
                    onSubmit={event => {
                        event.preventDefault()
                        void submitMessage()
                    }}
                >
                    {replyingTo && (
                        <div className={styles.replyArmed}>
                            <span className={styles.replyRule} aria-hidden />
                            {replyingTo.thumbnailUrl && (
                                <img
                                    className={styles.replyThumbnail}
                                    src={replyingTo.thumbnailUrl}
                                    alt=""
                                />
                            )}
                            <span className={styles.replyArmedContent}>
                                <strong>Replying to {replyingTo.name}</strong>
                                <span>{replyingTo.text}</span>
                            </span>
                            <button
                                type="button"
                                onClick={() => setReplyingTo(null)}
                                aria-label="Cancel reply"
                            >
                                <X size={16} strokeWidth={2} aria-hidden />
                            </button>
                        </div>
                    )}
                    <div className={styles.composerRow}>
                        <button
                            type="button"
                            className={openMenu === 'plus' ? styles.menuButtonActive : undefined}
                            onClick={() =>
                                setOpenMenu(current => (current === 'plus' ? null : 'plus'))
                            }
                            aria-label={
                                openMenu === 'plus' ? 'Close attachment menu' : 'Add attachment'
                            }
                            aria-expanded={openMenu === 'plus'}
                            disabled={!quest || sending}
                        >
                            {openMenu === 'plus' ? (
                                <X size={21} strokeWidth={2.2} aria-hidden />
                            ) : (
                                <Plus size={21} strokeWidth={2.2} aria-hidden />
                            )}
                        </button>
                        <input
                            ref={messageInputRef}
                            className={styles.messageInput}
                            value={message}
                            onChange={event => setMessage(event.target.value)}
                            onKeyDown={event => {
                                if (event.key === 'Backspace' && !message && replyingTo) {
                                    event.preventDefault()
                                    setReplyingTo(null)
                                    return
                                }
                                if (event.key === 'Enter' && !event.shiftKey) {
                                    event.preventDefault()
                                    void submitMessage()
                                }
                            }}
                            placeholder="Message about this ordinal"
                            aria-label="Message about this ordinal"
                            disabled={!quest || sending}
                        />
                        <button
                            type="button"
                            className={openMenu === 'make' ? styles.menuButtonActive : undefined}
                            onClick={() =>
                                setOpenMenu(current => (current === 'make' ? null : 'make'))
                            }
                            aria-label={openMenu === 'make' ? 'Close Make menu' : 'Make'}
                            title="Make"
                            aria-expanded={openMenu === 'make'}
                            disabled={!quest || sending}
                        >
                            {openMenu === 'make' ? (
                                <X size={20} strokeWidth={2} aria-hidden />
                            ) : (
                                <Sparkles size={20} strokeWidth={1.9} aria-hidden />
                            )}
                        </button>
                        <button
                            type="submit"
                            className={styles.sendButton}
                            disabled={!quest || !message.trim() || sending}
                            aria-label="Send message"
                        >
                            {sending ? (
                                <Loader2 size={19} className={styles.spinner} aria-hidden />
                            ) : (
                                <ArrowUp size={21} strokeWidth={2.2} aria-hidden />
                            )}
                        </button>
                    </div>
                    <input
                        ref={cameraInputRef}
                        className={styles.hiddenInput}
                        type="file"
                        accept="image/*,video/*"
                        capture="environment"
                        onChange={event => {
                            const file = event.target.files?.[0]
                            event.target.value = ''
                            if (file) openExpandedComposer({ file })
                        }}
                    />
                    <input
                        ref={fileInputRef}
                        className={styles.hiddenInput}
                        type="file"
                        onChange={event => {
                            const file = event.target.files?.[0]
                            event.target.value = ''
                            if (file) openExpandedComposer({ file })
                        }}
                    />
                </form>
            </section>
        </div>
    )

    return createPortal(modal, document.body)
}
